/**
 * PestleWizard — PESTLE Assessment Editor
 * Allows users to analyse each PESTLE category and manage factors.
 */

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { jsPDF } from 'jspdf';
import { pestleApi, riskApi, registersApi, threatApi, threatRegistersApi, linkedArtefactsApi, registerConfigApi, templateApi } from '../services/api';
import { useSwal } from '../hooks/useSwal';
import { useToast } from '../hooks/useToast';
import { PageHeader } from '../components/PageHeader';
import { NimbusModal, NimbusModalContent } from '../components/NimbusModal';

/** risk_title may be stored as a plain string, { value, label } object, or under various field paths */
const getRiskTitle = (r: any): string => {
  const raw = r.fields?.risk_title ?? r.fields?.title ?? r.title ?? r.rowTitle;
  if (!raw) return r.riskId || '';
  if (typeof raw === 'object' && 'value' in raw) return String(raw.value || raw.label || r.riskId);
  return String(raw);
};

type LinkedRisk = {
  riskId: string;
  riskTitle: string;
  registerId?: string;
};

type PestleFactor = {
  factorId: string;
  category: string;
  title: string;
  description: string;
  impact: 'low' | 'medium' | 'high' | 'critical';
  type: 'threat' | 'opportunity';
  notes: string;
  linkedRisks: LinkedRisk[];
};

type PestleAssessment = {
  pestleId: string;
  tenantId: string;
  title: string;
  description: string;
  categoryAnalyses?: Record<string, string>;
  status: 'draft' | 'complete';
  factors: PestleFactor[];
  createdAt: string;
  updatedAt: string;
  createdBy: string;
};

const CATEGORIES = ['Political', 'Economic', 'Social', 'Technological', 'Legal', 'Environmental', 'Security'];
const IMPACTS = [
  { value: 'low', label: 'Low', tone: 'neutral' },
  { value: 'medium', label: 'Medium', tone: 'warning' },
  { value: 'high', label: 'High', tone: 'danger' },
  { value: 'critical', label: 'Critical', tone: 'danger' },
];
const TYPES = [
  { value: 'threat', label: 'Threat', tone: 'danger' },
  { value: 'opportunity', label: 'Opportunity', tone: 'success' },
];

export const PestleWizard: React.FC = () => {
  const { pestleId } = useParams<{ pestleId: string }>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const swal = useSwal();
  const toast = useToast();

  const [selectedTab, setSelectedTab] = useState(0);
  const [assessment, setAssessment] = useState<PestleAssessment | null>(null);
  const initialLoadDone = useRef(false);
  const [addingFactor, setAddingFactor] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [newFactor, setNewFactor] = useState({
    title: '',
    description: '',
    impact: 'medium' as const,
    type: 'threat' as const,
    notes: '',
    linkedRisks: [] as LinkedRisk[],
  });
  const [titleEditing, setTitleEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [riskSearch, setRiskSearch] = useState<Record<string, string>>({});
  const [showRiskDropdown, setShowRiskDropdown] = useState<string | null>(null);
  const [editingFactor, setEditingFactor] = useState<string | null>(null);
  const [editFactorData, setEditFactorData] = useState<Omit<PestleFactor, 'factorId' | 'category'>>({
    title: '', description: '', impact: 'medium', type: 'threat', notes: '', linkedRisks: [],
  });
  const [migratingFactor, setMigratingFactor] = useState<PestleFactor | null>(null);
  const [migrateRegisterId, setMigrateRegisterId] = useState('');
  const [migrateLoading, setMigrateLoading] = useState(false);
  const [showNewRegisterForm, setShowNewRegisterForm] = useState(false);
  const [newRegisterName, setNewRegisterName] = useState('');
  const [addingRiskForFactor, setAddingRiskForFactor] = useState<PestleFactor | null>(null);
  const [newRiskTitle, setNewRiskTitle] = useState('');
  const [newRiskRegisterId, setNewRiskRegisterId] = useState('');
  const [addingRiskLoading, setAddingRiskLoading] = useState(false);
  const [showNewRiskRegisterForm, setShowNewRiskRegisterForm] = useState(false);
  const [newRiskRegisterName, setNewRiskRegisterName] = useState('');
  const [newRiskRegisterLoading, setNewRiskRegisterLoading] = useState(false);

  // Load assessment
  const { data, isLoading, error } = useQuery({
    queryKey: ['pestle-assessment', pestleId],
    queryFn: () => pestleId ? pestleApi.get(pestleId) : Promise.resolve({ assessment: null }),
    enabled: !!pestleId,
  });

  // Load all risks for tenant
  const { data: allRisksData } = useQuery({
    queryKey: ['risks-all'],
    queryFn: () => riskApi.listAll(),
  });
  const allRisks = allRisksData || [];

  // Load risk registers for friendly name display
  const { data: riskRegisters = [] } = useQuery({
    queryKey: ['registers-risk'],
    queryFn: registersApi.getAll,
  });
  const registerNameById = useMemo(() => {
    const m = new Map<string, string>();
    riskRegisters.forEach((reg: any) => m.set(reg.registerId, reg.name || reg.registerId));
    return m;
  }, [riskRegisters]);

  // Load threat registers for the migrate-to-threat feature
  const { data: threatRegisters = [], refetch: refetchThreatRegisters } = useQuery({
    queryKey: ['threat-registers'],
    queryFn: threatRegistersApi.getAll,
  });

  const handleCreateRegister = async () => {
    if (!newRegisterName.trim()) return;
    try {
      const result = await threatRegistersApi.create({ name: newRegisterName.trim() });
      const newId = result?.registerId || result?.register?.registerId;
      if (newId) {
        try {
          const templates = await templateApi.list('threat');
          const standard = templates.find((t: any) => t.templateName?.toLowerCase() === 'standard') ?? templates[0];
          if (standard) await registerConfigApi.applyTemplate('threat', newId, standard.templateName);
        } catch { /* template apply is best-effort */ }
      }
      await refetchThreatRegisters();
      if (newId) setMigrateRegisterId(newId);
      setShowNewRegisterForm(false);
      setNewRegisterName('');
      toast.success('Threat register created.');
    } catch {
      toast.error('Failed to create threat register.');
    }
  };

  const handleCreateRiskRegister = async () => {
    if (!newRiskRegisterName.trim()) return;
    setNewRiskRegisterLoading(true);
    try {
      const result = await registersApi.create({ name: newRiskRegisterName.trim() });
      const newId = result?.registerId || result?.register?.registerId;
      if (newId) {
        try {
          const templates = await templateApi.list('risk');
          const standard = templates.find((t: any) => t.templateName?.toLowerCase() === 'standard') ?? templates[0];
          if (standard) await registerConfigApi.applyTemplate('risk', newId, standard.templateName);
        } catch { /* best-effort */ }
        queryClient.invalidateQueries({ queryKey: ['registers-risk'] });
        setNewRiskRegisterId(newId);
      }
      setShowNewRiskRegisterForm(false);
      setNewRiskRegisterName('');
      toast.success('Risk register created.');
    } catch {
      toast.error('Failed to create risk register.');
    } finally {
      setNewRiskRegisterLoading(false);
    }
  };

  const handleMigrateToThreat = async () => {
    if (!migratingFactor || !migrateRegisterId) return;
    setMigrateLoading(true);
    try {
      const created = await threatApi.create({
        registerId: migrateRegisterId,
        status: 'active',
        fields: {
          threat_name: migratingFactor.title,
          threat_description: migratingFactor.description || '',
          threat_notes: migratingFactor.notes || '',
          threat_priority: migratingFactor.impact,
        },
      });
      const threatId = created?.threatId || created?.threat?.threatId;
      if (!threatId) throw new Error('No threatId returned');

      // Link each associated risk to the new threat
      const linkedRisks = migratingFactor.linkedRisks || [];
      if (linkedRisks.length > 0) {
        await linkedArtefactsApi.createLinks({
          source_id: threatId,
          source_type: 'threat',
          source_register: migrateRegisterId,
          target_artefacts: linkedRisks.map(lr => ({
            id: lr.riskId,
            type: 'risk',
            register: lr.registerId || '',
          })),
        });
      }

      toast.success(`Threat created${linkedRisks.length > 0 ? ` and ${linkedRisks.length} risk${linkedRisks.length !== 1 ? 's' : ''} linked` : ''}.`);
      setMigratingFactor(null);
      setMigrateRegisterId('');
      navigate(`/threats/${threatId}?registerId=${migrateRegisterId}`);
    } catch (err) {
      console.error(err);
      toast.error('Failed to create threat.');
    } finally {
      setMigrateLoading(false);
    }
  };

  // Update local state on initial load only — subsequent cache updates (from saves)
  // should not overwrite in-progress edits
  useEffect(() => {
    if (data?.assessment && !initialLoadDone.current) {
      initialLoadDone.current = true;
      setAssessment(data.assessment);
      setEditTitle(data.assessment.title);
    }
  }, [data]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (updatedAssessment: PestleAssessment) =>
      pestleApi.update(updatedAssessment.pestleId, {
        title: updatedAssessment.title,
        description: updatedAssessment.description,
        categoryAnalyses: updatedAssessment.categoryAnalyses ?? {},
        status: updatedAssessment.status,
        factors: updatedAssessment.factors,
      }),
    onSuccess: (savedData) => {
      // Keep detail cache current so navigating away & back restores the correct text
      queryClient.setQueryData(['pestle-assessment', pestleId], savedData);
      queryClient.invalidateQueries({ queryKey: ['pestle-assessments'] });
    },
    onError: () => toast.error('Failed to save assessment'),
  });

  // Get factors for current category
  const categoryFactors = useMemo(() => {
    if (!assessment) return [];
    const cat = CATEGORIES[selectedTab];
    return assessment.factors.filter(f => f.category === cat);
  }, [assessment, selectedTab]);

  const handleAddFactor = () => {
    if (!assessment) return;
    if (!newFactor.title.trim()) {
      toast.error('Factor title is required');
      return;
    }

    const factor: PestleFactor = {
      factorId: Math.random().toString(36).substr(2, 9),
      category: CATEGORIES[selectedTab],
      ...newFactor,
    };

    const updated = {
      ...assessment,
      factors: [...assessment.factors, factor],
    };
    setAssessment(updated);
    saveMutation.mutate(updated);
    setNewFactor({
      title: '',
      description: '',
      impact: 'medium',
      type: 'threat',
      notes: '',
      linkedRisks: [],
    });
    setAddingFactor(false);
  };

  const handleDeleteFactor = (factorId: string) => {
    if (!assessment) return;
    const updated = {
      ...assessment,
      factors: assessment.factors.filter(f => f.factorId !== factorId),
    };
    setAssessment(updated);
    saveMutation.mutate(updated);
  };

  const handleLinkRisk = (factorId: string, risk: any) => {
    const riskTitle = getRiskTitle(risk);
    const linked: LinkedRisk = { riskId: risk.riskId, riskTitle, registerId: risk.registerId };
    if (editingFactor === factorId) {
      setEditFactorData(prev => ({ ...prev, linkedRisks: [...(prev.linkedRisks || []).filter(r => r.riskId !== risk.riskId), linked] }));
      setShowRiskDropdown(null);
      setRiskSearch(prev => ({ ...prev, [factorId]: '' }));
      return;
    }
    if (!assessment) return;
    const updated = {
      ...assessment,
      factors: assessment.factors.map(f =>
        f.factorId === factorId
          ? { ...f, linkedRisks: [...(f.linkedRisks || []).filter(r => r.riskId !== risk.riskId), linked] }
          : f
      ),
    };
    setAssessment(updated);
    setShowRiskDropdown(null);
    setRiskSearch(prev => ({ ...prev, [factorId]: '' }));
  };

  const handleCreateAndLinkRisk = async () => {
    if (!addingRiskForFactor || !newRiskTitle.trim() || !newRiskRegisterId) return;
    if (!assessment) return;
    setAddingRiskLoading(true);
    try {
      const created = await riskApi.create({
        registerId: newRiskRegisterId,
        // Send both common title keys so it works regardless of how the register field is configured
        fields: { title: newRiskTitle.trim(), risk_title: newRiskTitle.trim() },
      });
      const riskId = created?.riskId || created?.risk?.riskId;
      if (!riskId) throw new Error('No riskId returned');

      const linked: LinkedRisk = { riskId, riskTitle: newRiskTitle.trim(), registerId: newRiskRegisterId };
      const factorId = addingRiskForFactor.factorId;

      // Build updated assessment directly so we can save immediately
      const updatedAssessment: PestleAssessment = {
        ...assessment,
        factors: assessment.factors.map(f =>
          f.factorId === factorId
            ? { ...f, linkedRisks: [...(f.linkedRisks || []).filter(r => r.riskId !== riskId), linked] }
            : f
        ),
      };

      // Update local state and persist to backend in one step
      setAssessment(updatedAssessment);
      await saveMutation.mutateAsync(updatedAssessment);

      queryClient.invalidateQueries({ queryKey: ['risks-all'] });
      setAddingRiskForFactor(null);
      setNewRiskTitle('');
    } catch (e: any) {
      const msg = e?.message || 'Failed to create risk.';
      toast.error(msg);
    } finally {
      setAddingRiskLoading(false);
    }
  };

  const handleUnlinkRisk = (factorId: string, riskId: string) => {
    if (editingFactor === factorId) {
      setEditFactorData(prev => ({ ...prev, linkedRisks: (prev.linkedRisks || []).filter(r => r.riskId !== riskId) }));
      return;
    }
    if (!assessment) return;
    const updated = {
      ...assessment,
      factors: assessment.factors.map(f =>
        f.factorId === factorId
          ? { ...f, linkedRisks: (f.linkedRisks || []).filter(r => r.riskId !== riskId) }
          : f
      ),
    };
    setAssessment(updated);
  };

  const handleStartEditFactor = (factor: PestleFactor) => {
    setEditingFactor(factor.factorId);
    setEditFactorData({
      title: factor.title,
      description: factor.description,
      impact: factor.impact,
      type: factor.type,
      notes: factor.notes,
      linkedRisks: factor.linkedRisks || [],
    });
    setAddingFactor(false);
  };

  const handleSaveEditFactor = (factorId: string) => {
    if (!editFactorData.title.trim()) { toast.error('Factor title is required'); return; }
    if (!assessment) return;
    const updated = {
      ...assessment,
      factors: assessment.factors.map(f =>
        f.factorId === factorId ? { ...f, ...editFactorData } : f
      ),
    };
    setAssessment(updated);
    saveMutation.mutate(updated);
    setEditingFactor(null);
  };

  const handleExportPDF = async () => {
    if (!assessment) return;
    setExporting(true);
    try {
      const esc = (s?: string | null) => (s || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
      const genDate = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
      const fileDate = new Date().toISOString().slice(0, 10);

      const opportunityCount = assessment.factors.filter(f => f.type === 'opportunity').length;
      const threatCount = assessment.factors.filter(f => f.type === 'threat').length;
      const categoriesWithFactors = new Set(assessment.factors.map(f => f.category)).size;

      const sc = (label: string, value: string | number, color: string) =>
        `<div style="display:inline-block;width:calc(25% - 8px);margin:0 4px 8px 0;padding:10px 14px;background:#f9fafb;border:1px solid #e5e7eb;border-radius:6px;vertical-align:top;text-align:center;">` +
        `<span style="font-size:20px;font-weight:700;color:${color};">${value}</span><br>` +
        `<span style="font-size:11px;color:#6b7280;">${label}</span></div>`;

      const typeColor = (t: string) => t === 'opportunity' ? '#15803d' : '#dc2626';
      const typeBg   = (t: string) => t === 'opportunity' ? '#dcfce7' : '#fee2e2';
      const impactColor = (i: string) => i === 'critical' || i === 'high' ? '#dc2626' : i === 'medium' ? '#d97706' : '#6b7280';
      const impactBg    = (i: string) => i === 'critical' || i === 'high' ? '#fee2e2' : i === 'medium' ? '#fef3c7' : '#f3f4f6';

      const categorySections = CATEGORIES.map(category => {
        const catFactors = assessment.factors.filter(f => f.category === category);
        const factorCards = catFactors.length === 0
          ? `<div style="padding:16px 20px;font-size:12px;color:#9ca3af;font-style:italic;">No factors added for this category.</div>`
          : catFactors.map(factor => {
              const linkedHtml = (factor.linkedRisks || []).length > 0
                ? `<p style="margin:8px 0 0 0;"><span style="font-size:10px;font-weight:700;color:#6b7280;text-transform:uppercase;letter-spacing:0.06em;">Linked Risks</span><br>` +
                  factor.linkedRisks.map(lr => {
                    const live = allRisks.find(r => r.riskId === lr.riskId);
                    const title = live ? getRiskTitle(live) : lr.riskTitle;
                    return `<span style="font-size:12px;color:#374151;">&#8226; ${esc(lr.riskId)} \u2014 ${esc(title)}</span><br>`;
                  }).join('') + `</p>`
                : '';
              return `<div style="padding:14px 20px;border-bottom:1px solid #f3f4f6;">` +
                `<table style="width:100%;border-collapse:collapse;"><tr>` +
                `<td style="vertical-align:top;padding-right:12px;">` +
                `<p style="margin:0 0 4px 0;font-size:13px;font-weight:700;color:#111827;">${esc(factor.title)}</p>` +
                (factor.description ? `<p style="margin:0 0 4px 0;font-size:12px;color:#374151;line-height:1.5;">${esc(factor.description)}</p>` : '') +
                (factor.notes ? `<p style="margin:0;font-size:11px;color:#6b7280;font-style:italic;">Notes: ${esc(factor.notes)}</p>` : '') +
                linkedHtml +
                `</td>` +
                `<td style="vertical-align:top;text-align:right;white-space:nowrap;width:160px;">` +
                `<span style="display:inline-block;padding:2px 10px;border-radius:12px;font-size:11px;font-weight:600;background:${typeBg(factor.type)};color:${typeColor(factor.type)};margin-bottom:4px;">${esc(factor.type.charAt(0).toUpperCase() + factor.type.slice(1))}</span><br>` +
                `<span style="display:inline-block;padding:2px 10px;border-radius:12px;font-size:11px;font-weight:600;background:${impactBg(factor.impact)};color:${impactColor(factor.impact)};">${esc(factor.impact.charAt(0).toUpperCase() + factor.impact.slice(1))}</span>` +
                `</td></tr></table>` +
                `</div>`;
            }).join('');

        const catAnalysis = (assessment.categoryAnalyses ?? {})[category]?.trim();
        const catAnalysisHtml = catAnalysis
          ? `<div style="padding:12px 20px;font-size:12px;color:#374151;line-height:1.6;border-bottom:1px solid #f3f4f6;">${esc(catAnalysis)}</div>`
          : '';

        return `<div style="border:1px solid #e5e7eb;border-radius:8px;margin-bottom:16px;">` +
          `<div style="background:#f9fafb;border-bottom:1px solid #e5e7eb;padding:12px 16px;">` +
          `<span style="font-size:13px;font-weight:700;color:#111827;">${category}</span>` +
          `<span style="margin-left:8px;padding:2px 8px;border-radius:12px;font-size:11px;font-weight:600;background:#e5e7eb;color:#374151;">${catFactors.length} factor${catFactors.length !== 1 ? 's' : ''}</span>` +
          `</div>` +
          catAnalysisHtml +
          factorCards +
          `</div>`;
      }).join('');

      const statusBadge = assessment.status === 'complete'
        ? `<span style="padding:3px 10px;border-radius:12px;font-size:11px;font-weight:600;background:#dcfce7;color:#15803d;">Complete</span>`
        : `<span style="padding:3px 10px;border-radius:12px;font-size:11px;font-weight:600;background:#fef9c3;color:#854d0e;">Draft</span>`;

      const descHtml = assessment.description?.trim()
        ? `<div style="margin-bottom:24px;padding:16px 20px;background:#fafafa;border:1px solid #e5e7eb;border-radius:8px;font-size:13px;color:#374151;line-height:1.6;">${esc(assessment.description.trim())}</div>`
        : '';

      const html =
        `<div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif;background:#ffffff;color:#111827;width:860px;padding:40px;">` +
        `<div style="padding:32px;background:#fafafa;border:1px solid #e5e7eb;border-radius:8px;margin-bottom:32px;">` +
        `<div style="margin-bottom:8px;">${statusBadge}</div>` +
        `<h1 style="font-size:28px;font-weight:800;color:#111827;margin:0 0 8px 0;">${esc(assessment.title)}</h1>` +
        `<div style="margin-bottom:20px;">` +
        `<span style="padding:4px 12px;border-radius:20px;font-size:12px;font-weight:600;color:#5b21b6;background:#ede9fe;">ISO 27001:2022 \u2014 Clause 4.1 / 6.1</span>` +
        `<span style="font-size:13px;color:#6b7280;margin-left:12px;">Generated ${genDate}</span>` +
        `</div>` +
        sc('Categories', `${categoriesWithFactors}/${CATEGORIES.length}`, '#111827') +
        sc('Total Factors', assessment.factors.length, '#0891b2') +
        sc('Opportunities', opportunityCount, '#15803d') +
        sc('Threats', threatCount, '#dc2626') +
        `</div>` +
        descHtml +
        categorySections +
        `<table style="width:100%;border-collapse:collapse;margin-top:32px;"><tr>` +
        `<td style="padding-top:16px;border-top:1px solid #e5e7eb;font-size:11px;color:#9ca3af;">${esc(assessment.title)} \u00b7 PESTLE Assessment \u00b7 ISO/IEC 27001:2022</td>` +
        `<td style="padding-top:16px;border-top:1px solid #e5e7eb;text-align:right;font-size:11px;color:#9ca3af;">Generated ${genDate}</td>` +
        `</tr></table>` +
        `</div>`;

      const wrapper = document.createElement('div');
      wrapper.style.cssText = 'position:absolute;top:99999px;left:0;width:860px;';
      wrapper.innerHTML = html;
      document.body.appendChild(wrapper);
      void wrapper.getBoundingClientRect();
      const fullH = wrapper.scrollHeight;
      const { default: html2canvas } = await import('html2canvas');
      const canvas = await html2canvas(wrapper, { scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff', width: 860, height: fullH, windowWidth: 860, windowHeight: fullH });
      document.body.removeChild(wrapper);
      const margin = 10, printW = 190;
      const imgH = canvas.height / 2;
      const ratio = printW / (canvas.width / 2);
      const docH = imgH * ratio + margin * 2;
      const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: [210, Math.max(docH, 210.1)] });
      pdf.addImage(canvas.toDataURL('image/jpeg', 0.95), 'JPEG', margin, margin, printW, imgH * ratio);
      pdf.save(`${assessment.title.replace(/[^a-z0-9]/gi, '_')}_PESTLE_${fileDate}.pdf`);
    } catch (err) {
      console.error(err);
    } finally {
      setExporting(false);
    }
  };

  const handleSave = () => {
    if (!assessment) return;
    saveMutation.mutate(assessment, { onSuccess: () => toast.success('Assessment updated.') });
  };

  const handleComplete = async () => {
    if (!assessment) return;
    if (assessment.factors.length === 0) {
      toast.error('Add at least one factor before completing');
      return;
    }
    const confirmed = await swal.confirm('Mark assessment as complete?', 'You can still edit it after marking complete.');
    if (confirmed) {
      const updated = {
        ...assessment,
        status: 'complete' as const,
      };
      setAssessment(updated);
      saveMutation.mutate(updated);
    }
  };

  const handleUpdateTitle = () => {
    if (!assessment) return;
    if (!editTitle.trim()) {
      toast.error('Title cannot be empty');
      return;
    }
    const updated = {
      ...assessment,
      title: editTitle.trim(),
    };
    setAssessment(updated);
    setTitleEditing(false);
    saveMutation.mutate(updated);
  };

  if (isLoading) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !assessment) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y">
        <div className="np-banner np-tone-danger">
          <i className="ri-error-warning-line"></i>
          <span>Failed to load assessment. <a href="/pestle">Back to list</a></span>
        </div>
      </div>
    );
  }

  const currentCategory = CATEGORIES[selectedTab];
  const completedCategories = CATEGORIES.filter(cat => assessment.factors.some(f => f.category === cat)).length;
  const opportunitiesCount = assessment.factors.filter(f => f.type === 'opportunity').length;
  const threatsCount = assessment.factors.filter(f => f.type === 'threat').length;

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-mind-map"
        title={titleEditing ? '' : assessment.title}
        subtitle={!titleEditing ? currentCategory + ' Analysis' : ''}
        colour="info"
        actions={
          <div className="d-flex gap-2">
            <button
              className="np-btn np-btn-ghost"
              onClick={handleExportPDF}
              disabled={exporting}
              title="Download as PDF"
            >
              {exporting ? <span className="spinner-border spinner-border-sm"></span> : <i className="ri-file-pdf-line"></i>}
              {exporting ? 'Exporting…' : 'PDF'}
            </button>
            <button
              className="np-btn np-btn-ghost"
              onClick={handleSave}
              disabled={saveMutation.isPending}
            >
              <i className="ri-save-line"></i>
              {saveMutation.isPending ? 'Saving…' : 'Save'}
            </button>
            {assessment.status === 'draft' && (
              <button
                className="np-btn np-btn-success"
                onClick={handleComplete}
                disabled={saveMutation.isPending || assessment.factors.length === 0}
              >
                <i className="ri-checkbox-circle-line"></i>
                Mark Complete
              </button>
            )}
            {assessment.status === 'complete' && (
              <span className="np-chip np-tone-success">
                <i className="ri-checkbox-circle-line"></i>Completed
              </span>
            )}
          </div>
        }
      />

      {/* Title edit inline */}
      {titleEditing && (
        <div className="np-card mb-4">
          <div className="np-card-body">
            <div className="d-flex gap-2">
              <input
                type="text"
                className="np-input"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleUpdateTitle()}
                autoFocus
              />
              <button className="np-btn np-btn-primary" onClick={handleUpdateTitle}>
                <i className="ri-check-line"></i>Save
              </button>
              <button className="np-btn np-btn-ghost" onClick={() => setTitleEditing(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="np-grid np-grid-4">
        <div className="np-card">
          <div className="np-card-body np-stat">
            <span className="np-stat-icon np-tone-info"><i className="ri-mind-map"></i></span>
            <p className="np-stat-label">Categories</p>
            <p className="np-stat-value np-tabular">{completedCategories}/{CATEGORIES.length}</p>
          </div>
        </div>
        <div className="np-card">
          <div className="np-card-body np-stat">
            <span className="np-stat-icon np-tone-success"><i className="ri-lightbulb-line"></i></span>
            <p className="np-stat-label">Opportunities</p>
            <p className="np-stat-value np-tabular">{opportunitiesCount}</p>
          </div>
        </div>
        <div className="np-card">
          <div className="np-card-body np-stat">
            <span className="np-stat-icon np-tone-danger"><i className="ri-error-warning-line"></i></span>
            <p className="np-stat-label">Threats</p>
            <p className="np-stat-value np-tabular">{threatsCount}</p>
          </div>
        </div>
        <div className="np-card">
          <div className="np-card-body np-stat">
            <span className="np-stat-icon np-tone-warning"><i className="ri-file-text-line"></i></span>
            <p className="np-stat-label">Total Factors</p>
            <p className="np-stat-value np-tabular">{assessment.factors.length}</p>
          </div>
        </div>
      </div>

      {/* PESTLE Tabs */}
      <div className="np-card">
        {/* Tabs header */}
        <div className="np-tabs" style={{ padding: '1.125rem 1.25rem 0', overflowX: 'auto', flexWrap: 'nowrap' }}>
          {CATEGORIES.map((cat, idx) => (
            <button
              key={cat}
              type="button"
              className={`np-tab d-flex align-items-center gap-1${idx === selectedTab ? ' active' : ''}`}
              style={{ whiteSpace: 'nowrap' }}
              onClick={() => { if (assessment) saveMutation.mutate(assessment); setSelectedTab(idx); }}
            >
              {cat}
              {assessment.factors.some(f => f.category === cat) && (
                <span className="np-chip np-tone-success np-tabular">
                  {assessment.factors.filter(f => f.category === cat).length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="np-card-body">
          <div className="np-field">
            <label>
              <i className="ri-file-text-line me-2"></i>
              {currentCategory} Analysis
            </label>
            <textarea
              className="np-textarea"
              rows={4}
              placeholder={`Describe the ${currentCategory} factors that impact your organisation…`}
              value={(assessment.categoryAnalyses ?? {})[currentCategory] ?? ''}
              onChange={(e) => setAssessment({ ...assessment, categoryAnalyses: { ...(assessment.categoryAnalyses ?? {}), [currentCategory]: e.target.value } })}
              onBlur={handleSave}
            />
          </div>

          {/* Factors list */}
          <div className="mb-4">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h6 className="mb-0">
                <i className="ri-list-check-2 me-2"></i>
                Factors
                <span className="np-chip np-tone-neutral ms-2">{categoryFactors.length}</span>
              </h6>
              <button
                className="np-btn np-btn-primary np-btn-sm"
                onClick={() => { setAddingFactor(true); setEditingFactor(null); }}
                disabled={addingFactor}
              >
                <i className="ri-add-line"></i>Add Factor
              </button>
            </div>

            {/* Add factor form */}
            {addingFactor && (
              <div className="np-row-card mb-3">
                <div>
                  <div className="row g-3">
                    <div className="col-12 np-field">
                      <label>Factor Title</label>
                      <input
                        type="text"
                        className="np-input"
                        placeholder="e.g., New data protection regulations"
                        value={newFactor.title}
                        onChange={(e) => setNewFactor({ ...newFactor, title: e.target.value })}
                        autoFocus
                      />
                    </div>
                    <div className="col-12 np-field">
                      <label>Description</label>
                      <input
                        type="text"
                        className="np-input"
                        placeholder="Brief description of the factor"
                        value={newFactor.description}
                        onChange={(e) => setNewFactor({ ...newFactor, description: e.target.value })}
                      />
                    </div>
                    <div className="col-sm-6 np-field">
                      <label>Type</label>
                      <select
                        className="np-select"
                        value={newFactor.type}
                        onChange={(e) => setNewFactor({ ...newFactor, type: e.target.value as any })}
                      >
                        {TYPES.map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-sm-6 np-field">
                      <label>Impact</label>
                      <select
                        className="np-select"
                        value={newFactor.impact}
                        onChange={(e) => setNewFactor({ ...newFactor, impact: e.target.value as any })}
                      >
                        {IMPACTS.map(i => (
                          <option key={i.value} value={i.value}>{i.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-12 np-field">
                      <label>Notes</label>
                      <textarea
                        className="np-textarea"
                        rows={2}
                        placeholder="Additional notes or context"
                        value={newFactor.notes}
                        onChange={(e) => setNewFactor({ ...newFactor, notes: e.target.value })}
                      />
                    </div>
                    <div className="col-12">
                      <div className="d-flex align-items-center justify-content-between mb-1">
                        <label className="mb-0 small fw-semibold">
                          <i className="ri-links-line me-1"></i>Linked Risks
                          {newFactor.linkedRisks.length > 0 && (
                            <span className="np-chip np-tone-neutral ms-1">{newFactor.linkedRisks.length}</span>
                          )}
                        </label>
                        <button
                          type="button"
                          className="np-btn np-btn-ghost np-btn-sm"
                          onClick={() => setShowRiskDropdown(showRiskDropdown === '__new__' ? null : '__new__')}
                        >
                          <i className="ri-add-line"></i>Link Risk
                        </button>
                      </div>
                      {showRiskDropdown === '__new__' && (() => {
                        const searchTerm = riskSearch['__new__'] || '';
                        const filtered = allRisks.filter(r => {
                          const title = getRiskTitle(r);
                          return title.toLowerCase().includes(searchTerm.toLowerCase()) &&
                            !newFactor.linkedRisks.some(lr => lr.riskId === r.riskId);
                        });
                        return (
                          <div className="mb-2">
                            <input
                              type="text"
                              className="np-input"
                              placeholder="Search risks by title…"
                              value={searchTerm}
                              onChange={(e) => setRiskSearch(prev => ({ ...prev, '__new__': e.target.value }))}
                              autoFocus
                            />
                            {allRisks.length === 0 ? (
                              <div className="np-hint"><i className="ri-shield-line me-1"></i>No risks found in any register.</div>
                            ) : filtered.length === 0 ? (
                              <div className="np-hint">
                                {searchTerm ? 'No matching risks.' : 'All available risks already linked.'}
                              </div>
                            ) : (
                              <div className="mt-1 border rounded" style={{ maxHeight: '180px', overflowY: 'auto' }}>
                                {filtered.slice(0, 20).map(r => (
                                  <div
                                    key={r.riskId}
                                    className="np-popover-item"
                                    onClick={() => {
                                      const linked = { riskId: r.riskId, riskTitle: getRiskTitle(r), registerId: r.registerId };
                                      setNewFactor(prev => ({ ...prev, linkedRisks: [...prev.linkedRisks.filter(x => x.riskId !== r.riskId), linked] }));
                                      setShowRiskDropdown(null);
                                      setRiskSearch(prev => ({ ...prev, '__new__': '' }));
                                    }}
                                  >
                                    <div>
                                      <div className="fw-semibold">{getRiskTitle(r)}</div>
                                      {r.registerId && <div className="np-hint" style={{ margin: 0 }}>Register: {r.registerId}</div>}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                      {newFactor.linkedRisks.length > 0 && (
                        <div className="np-table-wrap mt-1">
                          <table className="np-table" style={{ fontSize: '0.8rem' }}>
                            <thead>
                              <tr>
                                <th style={{ width: '30%' }}>Register</th>
                                <th style={{ width: '20%' }}>ID</th>
                                <th>Title</th>
                                <th style={{ width: '2rem' }}></th>
                              </tr>
                            </thead>
                            <tbody>
                              {newFactor.linkedRisks.map(lr => (
                                <tr
                                  key={lr.riskId}
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => navigate(`/risks/${lr.riskId}`)}
                                >
                                  <td className="np-sub">{lr.registerId ? (registerNameById.get(lr.registerId) || lr.registerId) : '—'}</td>
                                  <td><code className="np-sub">{lr.riskId}</code></td>
                                  <td>{(() => { const live = allRisks.find(r => r.riskId === lr.riskId); return live ? getRiskTitle(live) : lr.riskTitle; })()}</td>
                                  <td>
                                    <button
                                      type="button"
                                      className="np-icon-btn np-icon-btn-danger"
                                      title="Unlink"
                                      onClick={(e) => { e.stopPropagation(); setNewFactor(prev => ({ ...prev, linkedRisks: prev.linkedRisks.filter(x => x.riskId !== lr.riskId) })); }}
                                    >
                                      <i className="ri-close-line"></i>
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                    <div className="col-12 d-flex gap-2">
                      <button className="np-btn np-btn-primary np-btn-sm" onClick={handleAddFactor}>
                        <i className="ri-check-line"></i>Add
                      </button>
                      <button
                        className="np-btn np-btn-ghost np-btn-sm"
                        onClick={() => {
                          setAddingFactor(false);
                          setNewFactor({ title: '', description: '', impact: 'medium', type: 'threat', notes: '', linkedRisks: [] });
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Factors display */}
            {categoryFactors.length === 0 ? (
              <div className="np-banner np-tone-info mb-0">
                <i className="ri-information-line"></i>
                No factors added yet for {currentCategory}.
              </div>
            ) : (
              <div className="d-flex flex-column gap-2">
                {categoryFactors.map(factor => {
                  const impactBadge = IMPACTS.find(i => i.value === factor.impact);
                  const typeBadge = TYPES.find(t => t.value === factor.type);
                  const factorLinkedRisks = editingFactor === factor.factorId
                    ? (editFactorData.linkedRisks || [])
                    : (factor.linkedRisks || []);
                  const searchTerm = riskSearch[factor.factorId] || '';
                  const filteredRisks = allRisks.filter(r => {
                    const title = getRiskTitle(r);
                    return title.toLowerCase().includes(searchTerm.toLowerCase()) &&
                      !factorLinkedRisks.some(lr => lr.riskId === r.riskId);
                  });
                  return (
                    <div key={factor.factorId} className="border rounded-2 p-0 overflow-hidden">
                      {/* Factor header / inline edit */}
                      {editingFactor === factor.factorId ? (
                        <div className="p-2">
                          <div className="row g-2">
                            <div className="col-12 np-field">
                              <label>Factor Title</label>
                              <input
                                className="np-input"
                                value={editFactorData.title}
                                onChange={(e) => setEditFactorData(prev => ({ ...prev, title: e.target.value }))}
                                autoFocus
                              />
                            </div>
                            <div className="col-12 np-field">
                              <label>Description</label>
                              <input
                                className="np-input"
                                value={editFactorData.description}
                                onChange={(e) => setEditFactorData(prev => ({ ...prev, description: e.target.value }))}
                              />
                            </div>
                            <div className="col-sm-6 np-field">
                              <label>Type</label>
                              <select
                                className="np-select"
                                value={editFactorData.type}
                                onChange={(e) => setEditFactorData(prev => ({ ...prev, type: e.target.value as any }))}
                              >
                                {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                              </select>
                            </div>
                            <div className="col-sm-6 np-field">
                              <label>Impact</label>
                              <select
                                className="np-select"
                                value={editFactorData.impact}
                                onChange={(e) => setEditFactorData(prev => ({ ...prev, impact: e.target.value as any }))}
                              >
                                {IMPACTS.map(i => <option key={i.value} value={i.value}>{i.label}</option>)}
                              </select>
                            </div>
                            <div className="col-12 np-field">
                              <label>Notes</label>
                              <textarea
                                className="np-textarea"
                                rows={2}
                                value={editFactorData.notes}
                                onChange={(e) => setEditFactorData(prev => ({ ...prev, notes: e.target.value }))}
                              />
                            </div>
                            <div className="col-12 d-flex gap-2">
                              <button className="np-btn np-btn-primary np-btn-sm" onClick={() => handleSaveEditFactor(factor.factorId)}>
                                <i className="ri-check-line"></i>Save
                              </button>
                              <button className="np-btn np-btn-ghost np-btn-sm" onClick={() => setEditingFactor(null)}>
                                Cancel
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="d-flex gap-2 align-items-start p-2">
                          <div className="flex-grow-1 min-width-0">
                            <div className="d-flex align-items-center gap-2 mb-1">
                              <h6 className="mb-0 small">{factor.title}</h6>
                              {typeBadge && (
                                <span className={`np-chip np-tone-${typeBadge.tone}`}>{typeBadge.label}</span>
                              )}
                              {impactBadge && (
                                <span className={`np-chip np-tone-${impactBadge.tone}`}>{impactBadge.label}</span>
                              )}
                            </div>
                            {factor.description && (
                              <p className="np-sub small mb-1">{factor.description}</p>
                            )}
                            {factor.notes && (
                              <p className="np-sub small mb-0"><strong>Notes:</strong> {factor.notes}</p>
                            )}
                          </div>
                          {factor.type === 'threat' && (
                          <button
                            className="np-icon-btn np-icon-btn-success flex-shrink-0"
                            onClick={() => { setMigratingFactor(factor); setMigrateRegisterId(threatRegisters[0]?.registerId || ''); }}
                            title="Migrate to Threat Register"
                          >
                            <i className="ri-arrow-right-up-line"></i>
                          </button>
                          )}
                          <button
                            className="np-icon-btn flex-shrink-0"
                            onClick={() => handleStartEditFactor(factor)}
                            title="Edit factor"
                          >
                            <i className="ri-pencil-line"></i>
                          </button>
                          <button
                            className="np-icon-btn np-icon-btn-danger flex-shrink-0"
                            onClick={() => handleDeleteFactor(factor.factorId)}
                            title="Delete factor"
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      )}

                      {/* Linked risks */}
                      <div className="px-2 pb-2 border-top pt-2" style={{ background: 'var(--np-surface-2)' }}>
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <small className="np-sub fw-semibold">
                            <i className="ri-links-line me-1"></i>
                            Linked Risks
                            {factorLinkedRisks.length > 0 && (
                              <span className="np-chip np-tone-neutral ms-1">{factorLinkedRisks.length}</span>
                            )}
                          </small>
                          <div className="d-flex gap-1">
                            <button
                              className="np-btn np-btn-ghost np-btn-sm"
                              onClick={() => {
                                setShowRiskDropdown(showRiskDropdown === factor.factorId ? null : factor.factorId);
                              }}
                            >
                              <i className="ri-search-line"></i>Link Existing
                            </button>
                            <button
                              className="np-btn np-btn-ghost np-btn-sm"
                              onClick={() => {
                                setShowRiskDropdown(null);
                                setNewRiskTitle(factor.title || '');
                                setNewRiskRegisterId(riskRegisters.length > 0 ? (riskRegisters[0] as any).registerId : '');
                                setAddingRiskForFactor(factor);
                              }}
                            >
                              <i className="ri-add-line"></i>New Risk
                            </button>
                          </div>
                        </div>

                        {/* Risk search dropdown */}
                        {showRiskDropdown === factor.factorId && (
                          <div className="mb-2">
                            <input
                              type="text"
                              className="np-input"
                              placeholder="Search risks by title…"
                              value={searchTerm}
                              onChange={(e) => setRiskSearch(prev => ({ ...prev, [factor.factorId]: e.target.value }))}
                              autoFocus
                            />
                            {allRisks.length === 0 ? (
                              <div className="np-hint"><i className="ri-shield-line me-1"></i>No risks found in any register.</div>
                            ) : filteredRisks.length === 0 ? (
                              <div className="np-hint">
                                {searchTerm ? 'No matching risks.' : 'All available risks already linked.'}
                              </div>
                            ) : (
                              <div className="mt-1 border rounded" style={{ maxHeight: '180px', overflowY: 'auto' }}>
                                {filteredRisks.slice(0, 20).map(r => (
                                  <div
                                    key={r.riskId}
                                    className="np-popover-item"
                                    onClick={() => handleLinkRisk(factor.factorId, r)}
                                  >
                                    <div>
                                      <div className="fw-semibold">{getRiskTitle(r)}</div>
                                      {r.registerId && <div className="np-hint" style={{ margin: 0 }}>{registerNameById.get(r.registerId) || r.registerId}</div>}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Linked risk table */}
                        {factorLinkedRisks.length > 0 ? (
                          <div className="np-table-wrap">
                            <table className="np-table" style={{ fontSize: '0.8rem' }}>
                              <thead>
                                <tr>
                                  <th style={{ width: '30%' }}>Register</th>
                                  <th style={{ width: '20%' }}>ID</th>
                                  <th>Title</th>
                                  <th style={{ width: '2rem' }}></th>
                                </tr>
                              </thead>
                              <tbody>
                                {factorLinkedRisks.map(lr => (
                                  <tr
                                    key={lr.riskId}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => navigate(`/risks/${lr.riskId}`)}
                                  >
                                    <td className="np-sub">{lr.registerId ? (registerNameById.get(lr.registerId) || lr.registerId) : '—'}</td>
                                    <td><code className="np-sub">{lr.riskId}</code></td>
                                    <td>{(() => { const live = allRisks.find(r => r.riskId === lr.riskId); return live ? getRiskTitle(live) : lr.riskTitle; })()}</td>
                                    <td>
                                      <button
                                        className="np-icon-btn np-icon-btn-danger"
                                        title="Unlink"
                                        onClick={(e) => { e.stopPropagation(); handleUnlinkRisk(factor.factorId, lr.riskId); }}
                                      >
                                        <i className="ri-close-line"></i>
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="np-hint"><i className="ri-links-line me-1"></i>No risks linked.</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Navigation buttons */}
          <div className="d-flex gap-2">
            {selectedTab > 0 && (
              <button
                className="np-btn np-btn-ghost"
                onClick={() => setSelectedTab(selectedTab - 1)}
              >
                <i className="ri-arrow-left-line"></i>Previous
              </button>
            )}
            {selectedTab < CATEGORIES.length - 1 && (
              <button
                className="np-btn np-btn-ghost"
                onClick={() => setSelectedTab(selectedTab + 1)}
              >
                Next<i className="ri-arrow-right-line"></i>
              </button>
            )}
          </div>
        </div>
      </div>

      <p className="np-sub small mt-3">
        <i className="ri-information-line me-1"></i>
        Save as you go. You can mark the assessment complete and still edit it later.
      </p>

      {/* Migrate to Threat Register modal */}
      {/* Add Risk modal */}
      {addingRiskForFactor && (
        <NimbusModal centered>
          <NimbusModalContent
            title={<><i className="ri-add-circle-line me-2"></i>Add New Risk</>}
            onClose={() => { setAddingRiskForFactor(null); setNewRiskTitle(''); setShowNewRiskRegisterForm(false); setNewRiskRegisterName(''); }}
            footer={
              <>
                <button type="button" className="np-btn np-btn-ghost" onClick={() => { setAddingRiskForFactor(null); setNewRiskTitle(''); setShowNewRiskRegisterForm(false); setNewRiskRegisterName(''); }}>Cancel</button>
                <button
                  type="button"
                  className="np-btn np-btn-primary"
                  onClick={() => void handleCreateAndLinkRisk()}
                  disabled={addingRiskLoading || !newRiskTitle.trim() || !newRiskRegisterId}
                >
                  {addingRiskLoading
                    ? <><span className="spinner-border spinner-border-sm"></span>Creating…</>
                    : <><i className="ri-add-circle-line"></i>Create &amp; Link</>}
                </button>
              </>
            }
          >
                <p className="np-sub small mb-3">
                  Create a new risk and automatically link it to <strong>{addingRiskForFactor.title}</strong>.
                </p>
                <div className="np-field">
                  <div className="d-flex align-items-center justify-content-between mb-1">
                    <label className="mb-0">Risk Register <span className="text-danger">*</span></label>
                    <button type="button" className="np-btn np-btn-ghost np-btn-sm" onClick={() => setShowNewRiskRegisterForm(v => !v)}>
                      <i className="ri-add-line"></i>New Register
                    </button>
                  </div>
                  {showNewRiskRegisterForm && (
                    <div className="border rounded p-2 mb-2" style={{ background: 'var(--np-surface-2)' }}>
                      <div className="d-flex gap-2">
                        <input
                          className="np-input"
                          placeholder="Register name"
                          value={newRiskRegisterName}
                          onChange={e => setNewRiskRegisterName(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && void handleCreateRiskRegister()}
                          autoFocus
                        />
                        <button type="button" className="np-btn np-btn-primary np-btn-sm" onClick={() => void handleCreateRiskRegister()} disabled={!newRiskRegisterName.trim() || newRiskRegisterLoading}>
                          {newRiskRegisterLoading ? <span className="spinner-border spinner-border-sm" /> : 'Create'}
                        </button>
                        <button type="button" className="np-btn np-btn-ghost np-btn-sm" onClick={() => { setShowNewRiskRegisterForm(false); setNewRiskRegisterName(''); }}>Cancel</button>
                      </div>
                      <div className="np-hint"><i className="ri-information-line me-1"></i>The Standard template will be applied automatically.</div>
                    </div>
                  )}
                  {riskRegisters.length === 0 && !showNewRiskRegisterForm ? (
                    <div className="np-banner np-tone-warning">
                      <i className="ri-error-warning-line"></i>
                      No risk registers found. Use "New Register" above to create one.
                    </div>
                  ) : (
                    <select
                      className="np-select"
                      value={newRiskRegisterId}
                      onChange={e => setNewRiskRegisterId(e.target.value)}
                    >
                      {riskRegisters.map((r: any) => (
                        <option key={r.registerId} value={r.registerId}>{r.name || r.registerId}</option>
                      ))}
                    </select>
                  )}
                </div>
                <div className="np-field">
                  <label>Risk Title <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="np-input"
                    placeholder="Enter risk title…"
                    value={newRiskTitle}
                    onChange={e => setNewRiskTitle(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') void handleCreateAndLinkRisk(); if (e.key === 'Escape') { setAddingRiskForFactor(null); setNewRiskTitle(''); setShowNewRiskRegisterForm(false); setNewRiskRegisterName(''); } }}
                    autoFocus
                  />
                  <div className="np-hint">Pre-filled from the factor title — edit as needed.</div>
                </div>
          </NimbusModalContent>
        </NimbusModal>
      )}

      {migratingFactor && (
        <NimbusModal centered>
          <NimbusModalContent
            title={<><i className="ri-arrow-right-up-line me-2"></i>Migrate to Threat Register</>}
            onClose={() => { setMigratingFactor(null); setShowNewRegisterForm(false); setNewRegisterName(''); }}
            footer={
              <>
                <button type="button" className="np-btn np-btn-ghost" onClick={() => { setMigratingFactor(null); setShowNewRegisterForm(false); setNewRegisterName(''); }}>Cancel</button>
                <button
                  type="button"
                  className="np-btn np-btn-success"
                  onClick={handleMigrateToThreat}
                  disabled={migrateLoading || !migrateRegisterId || threatRegisters.length === 0}
                >
                  {migrateLoading
                    ? <><span className="spinner-border spinner-border-sm"></span>Creating…</>
                    : <><i className="ri-arrow-right-up-line"></i>Create Threat</>}
                </button>
              </>
            }
          >
                <p className="np-sub small mb-3">
                  This will create a new threat from <strong>{migratingFactor.title}</strong> and link its {migratingFactor.linkedRisks?.length || 0} associated risk(s) in the Linked Artefacts section.
                </p>
                <div className="np-field">
                  <div className="d-flex align-items-center justify-content-between mb-1">
                    <label className="mb-0">Target Threat Register <span className="text-danger">*</span></label>
                    <button type="button" className="np-btn np-btn-ghost np-btn-sm" onClick={() => setShowNewRegisterForm(v => !v)}>
                      <i className="ri-add-line"></i>New Register
                    </button>
                  </div>
                  {showNewRegisterForm && (
                    <div className="border rounded p-2 mb-2" style={{ background: 'var(--np-surface-2)' }}>
                      <div className="d-flex gap-2">
                        <input
                          className="np-input"
                          placeholder="Register name"
                          value={newRegisterName}
                          onChange={e => setNewRegisterName(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && handleCreateRegister()}
                          autoFocus
                        />
                        <button type="button" className="np-btn np-btn-primary np-btn-sm" onClick={handleCreateRegister} disabled={!newRegisterName.trim()}>Create</button>
                        <button type="button" className="np-btn np-btn-ghost np-btn-sm" onClick={() => { setShowNewRegisterForm(false); setNewRegisterName(''); }}>Cancel</button>
                      </div>
                    </div>
                  )}
                  {threatRegisters.length === 0 ? (
                    <div className="np-banner np-tone-warning">
                      <i className="ri-error-warning-line"></i>
                      No threat registers found. Use "New Register" above to create one.
                    </div>
                  ) : (
                    <select
                      className="np-select"
                      value={migrateRegisterId}
                      onChange={e => setMigrateRegisterId(e.target.value)}
                    >
                      {threatRegisters.map((r: any) => (
                        <option key={r.registerId} value={r.registerId}>{r.name || r.registerId}</option>
                      ))}
                    </select>
                  )}
                </div>
                <div className="border rounded p-2 small" style={{ background: 'var(--np-surface-2)' }}>
                  <div><strong>Factor:</strong> {migratingFactor.title}</div>
                  {migratingFactor.description && <div className="np-sub mt-1">{migratingFactor.description}</div>}
                  <div className="mt-1"><strong>Impact:</strong> {migratingFactor.impact} &nbsp;|&nbsp; <strong>Type:</strong> {migratingFactor.type}</div>
                  {(migratingFactor.linkedRisks?.length || 0) > 0 && (
                    <div className="mt-1"><strong>Risks to link:</strong> {migratingFactor.linkedRisks.map(lr => lr.riskTitle || lr.riskId).join(', ')}</div>
                  )}
                </div>
          </NimbusModalContent>
        </NimbusModal>
      )}
    </div>
  );
};
